import { useState, useEffect, useCallback, useMemo } from 'react'
import { Command } from 'cmdk'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { router, usePage } from '@inertiajs/react'
import { ArrowLeftRight, Bot, MessageSquarePlus, MessagesSquare, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  getCommandItems, getResourceItems, getSelectedWorkerItems, type CommandItem,
} from '@/frontend/lib/commandPaletteItems'
import { selectedWorkerStorageContextFromProps, useSelectedWorker } from '@/frontend/layouts/worker-first/useSelectedWorker'
import { visitWorkerPreservingPlace } from '@/frontend/layouts/worker-first/selectWorker'
import { useAgentChatSidebar } from '@/frontend/pages/Dashboard/AgentChats/agentChatSidebarStore'
import { persistedUiParam } from '@/frontend/pages/Dashboard/AgentChats/runtime/assistantUiToggle'
import { isEditableShortcutTarget } from '@/frontend/lib/keyboardShortcuts'
import type { SharedProps, NavigableWorker } from '@/frontend/types/inertia'

const groupHeadingClassName =
  '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground'

// Id do item "Trocar de agente", que abre o subview de agentes em vez de navegar.
const SWITCH_AGENT_ACTION_ID = 'acao-trocar-agente'

// Imperative open bridge (SZ-1603, B.2): shell chrome — the worker-first
// sidebar's ⌘K button — opens the palette without lifting its dialog state.
// Uses the same document the ⌘K keydown listener already lives on.
const OPEN_COMMAND_PALETTE_EVENT = 'sz:command-palette:open'

export function openCommandPalette(): void {
  document.dispatchEvent(new Event(OPEN_COMMAND_PALETTE_EVENT))
}

interface GroupConfig {
  key: string
  heading: string
  items: CommandItem[]
}

// Monta a URL do chat do agente preservando ?ui= — mesma semântica do
// agentChatHref do shell e do sessionHref do AssistantSessionList
// (montado aqui para não puxar o bundle do assistant-ui na paleta).
function agentChatHref(workerId: string, options: { sessionId?: string; newConversation?: boolean } = {}): string {
  const params = new URLSearchParams({ digital_worker_id: workerId })
  if (options.sessionId) params.set('session_id', options.sessionId)
  if (options.newConversation) params.set('new_conversation', '1')
  const ui = persistedUiParam()
  if (ui) params.set('ui', ui)
  return `/dashboard/agent_chats?${params.toString()}`
}

export default function CommandPalette() {
  const { props, url } = usePage<SharedProps>()
  const { auth, navigableResources, organizations, featureFlags } = props
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  // Subview do cmdk: 'root' mostra todos os grupos; 'agentes' é o submenu de
  // "Trocar de agente" (SZ-1594).
  const [view, setView] = useState<'root' | 'agentes'>('root')

  const workers = useMemo(() => navigableResources?.workers ?? [], [navigableResources])
  const selectedWorker = useSelectedWorker()
  const chatSnapshot = useAgentChatSidebar()
  const currentPath = url.split('?')[0]
  const selectionStorageContext = useMemo(
    () => selectedWorkerStorageContextFromProps(props),
    [auth.user?.id, auth.user?.active_organization?.id],
  )

  const staticItems = useMemo(() => {
    if (!auth.authenticated) return []
    return getCommandItems({
      orgSlug: auth.user?.active_organization?.slug ?? null,
      organizations: organizations ?? [],
      isAdmin: auth.user?.is_admin ?? false,
      discoveryLabel: props.navigationLabels.discovery,
      canAccessForge: props.permissions?.can_access_forge === true,
    })
  }, [auth.authenticated, auth.user?.active_organization?.slug, auth.user?.is_admin, organizations, props.navigationLabels.discovery, props.permissions?.can_access_forge])

  const resourceItems = useMemo(() => {
    if (!auth.authenticated) return []
    return getResourceItems(navigableResources ?? null)
  }, [auth.authenticated, navigableResources])

  // "Ir para": seções do agente selecionado (SZ-1594), com os mesmos destinos
  // e gates do WorkerNav.
  const workerSectionItems = useMemo(() => {
    if (!selectedWorker) return []
    return getSelectedWorkerItems(selectedWorker, {
      businessProcessesEnabled: featureFlags?.business_processes_enabled ?? false,
    })
  }, [selectedWorker, featureFlags?.business_processes_enabled])

  // "Conversas": sessões recentes do agente vindas do snapshot client-side do
  // chat (store SZ-1504) — sem nova chamada de rede. O snapshot só existe com a
  // página de chat montada; nas demais páginas não há fonte compartilhada com
  // as sessões, então o grupo simplesmente não aparece.
  const conversationItems = useMemo((): CommandItem[] => {
    if (!selectedWorker || !chatSnapshot || chatSnapshot.activeWorkerId !== selectedWorker.id) return []
    return chatSnapshot.sessions.slice(0, 5).map((session) => ({
      id: `conversa-${session.id}`,
      label: session.title,
      href: agentChatHref(selectedWorker.id, { sessionId: session.id }),
      icon: MessagesSquare,
      group: 'conversas' as const,
      keywords: [session.title, 'conversa', selectedWorker.name],
    }))
  }, [chatSnapshot, selectedWorker])

  // "Ações": nova conversa (com o gate de chat do WorkerFirstSidebar) e troca
  // de agente (submenu).
  const actionItems = useMemo((): CommandItem[] => {
    const items: CommandItem[] = []
    if (selectedWorker && selectedWorker.provisioning_status === 'ready' && selectedWorker.probe_connect_ready) {
      items.push({
        id: 'acao-nova-conversa',
        label: `Nova conversa com ${selectedWorker.name.trim().split(/\s+/)[0]}`,
        href: agentChatHref(selectedWorker.id, { newConversation: true }),
        icon: MessageSquarePlus,
        group: 'acoes',
        keywords: ['nova conversa', 'chat', selectedWorker.name],
      })
    }
    if (workers.length > 0) {
      items.push({
        id: SWITCH_AGENT_ACTION_ID,
        label: 'Trocar de agente',
        href: '',
        icon: ArrowLeftRight,
        group: 'acoes',
        keywords: ['trocar', 'agente', 'mudar', 'switch'],
        shortcut: '⌘1…9',
      })
    }
    return items
  }, [selectedWorker, workers.length])

  // Itens do submenu "Trocar de agente", com dicas ⌘1..⌘9 pela posição na lista.
  const agentSwitchItems = useMemo((): CommandItem[] => {
    return workers.map((worker, index) => ({
      id: `trocar-${worker.id}`,
      label: worker.name,
      subtitle: worker.id === selectedWorker?.id ? 'Agente atual' : worker.job_title ?? undefined,
      href: '',
      icon: Bot,
      group: 'acoes' as const,
      keywords: [worker.name, 'agente', 'trocar'],
      shortcut: index < 9 ? `⌘${index + 1}` : undefined,
    }))
  }, [workers, selectedWorker?.id])

  const allItems = useMemo(
    () => [...workerSectionItems, ...conversationItems, ...actionItems, ...staticItems, ...resourceItems],
    [workerSectionItems, conversationItems, actionItems, staticItems, resourceItems]
  )

  const groups = useMemo((): GroupConfig[] => {
    const groupOrder: Array<{ key: string; heading: string }> = [
      { key: 'ir-para', heading: selectedWorker ? `Ir para · ${selectedWorker.name}` : 'Ir para' },
      { key: 'conversas', heading: 'Conversas' },
      { key: 'acoes', heading: 'Ações' },
      { key: 'dashboard', heading: 'Páginas' },
      { key: 'organizacoes', heading: 'Organizações' },
      { key: 'agentes', heading: 'Agentes' },
      { key: 'credenciais', heading: 'Credenciais' },
      { key: 'composio', heading: 'Composio' },
      { key: 'admin', heading: 'Admin' },
      { key: 'admin-agentes', heading: 'Agentes (Admin)' },
      { key: 'admin-instancias', heading: 'Instâncias (Admin)' },
      { key: 'admin-telemetria', heading: 'Telemetria (Admin)' },
      { key: 'admin-wabas', heading: 'WABAs (Admin)' },
      { key: 'admin-orgs', heading: 'Telemetria - Organizações' },
    ]
    return groupOrder
      .map(g => ({ ...g, items: allItems.filter(i => i.group === g.key) }))
      .filter(g => g.items.length > 0)
  }, [allItems, selectedWorker])

  // Troca de agente pelo helper canônico do A.2 (visitWorkerPreservingPlace),
  // compartilhado com os links da sidebar. A palette é o atalho de troca:
  // re-selecionar o agente ativo é no-op; no chat a troca preserva
  // a página montada (preserveState — rascunho do composer sobrevive); em
  // seção de agente permanece na mesma seção do novo agente; caso contrário
  // abre o chat dele (ou o portal sem gateway) — SZ-1580/SZ-1594.
  const switchToWorker = useCallback((worker: NavigableWorker) => {
    if (worker.id === selectedWorker?.id) return
    visitWorkerPreservingPlace(worker, url, selectionStorageContext)
  }, [selectedWorker?.id, selectionStorageContext, url])

  // Atalhos globais: ⌘K abre/fecha a paleta; ⌘1..⌘9 troca de agente pela
  // posição em navigableResources.workers (B.6, SZ-1594).
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        if (isEditableShortcutTarget(e.target)) return
        e.preventDefault()
        setOpen(prev => !prev)
        return
      }
      // Só intercepta ⌘/Ctrl+dígito puro (sem Shift/Alt) e quando existe agente
      // naquela posição — caso contrário o atalho nativo do navegador (trocar
      // de aba) segue valendo. e.code cobre layouts onde e.key não é o dígito.
      if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey) return
      if (isEditableShortcutTarget(e.target)) return
      const digit = /^(?:Digit|Numpad)([1-9])$/.exec(e.code)?.[1]
      if (!digit) return
      const worker = workers[Number(digit) - 1]
      if (!worker) return
      e.preventDefault()
      if (e.repeat) return
      setOpen(false)
      switchToWorker(worker)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [workers, switchToWorker])

  // Close palette on Inertia navigation
  useEffect(() => {
    return router.on('navigate', () => setOpen(false))
  }, [])

  // Reabrir sempre parte do zero: busca vazia e visão raiz (vale também para a
  // abertura via ⌘K, que não passa pelo onOpenChange do Radix).
  useEffect(() => {
    if (open) {
      setSearch('')
      setView('root')
    }
  }, [open])

  // Open on the imperative bridge event (sidebar ⌘K button). The reset to a
  // fresh search/root view happens in the `open` effect above.
  useEffect(() => {
    const handleOpen = () => setOpen(true)
    document.addEventListener(OPEN_COMMAND_PALETTE_EVENT, handleOpen)
    return () => document.removeEventListener(OPEN_COMMAND_PALETTE_EVENT, handleOpen)
  }, [])

  const handleSelect = useCallback((value: string) => {
    if (value === SWITCH_AGENT_ACTION_ID) {
      setSearch('')
      setView('agentes')
      return
    }
    const switchTarget = workers.find(w => `trocar-${w.id}` === value)
    if (switchTarget) {
      setOpen(false)
      switchToWorker(switchTarget)
      return
    }
    const item = allItems.find(i => i.id === value)
    if (!item?.href) return
    setOpen(false)
    router.visit(item.href)
  }, [allItems, workers, switchToWorker])

  // Backspace com a busca vazia volta do submenu para a raiz (padrão cmdk).
  const handleInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (view === 'agentes' && search === '' && e.key === 'Backspace') {
      e.preventDefault()
      setView('root')
    }
  }, [view, search])

  if (!auth.authenticated) return null

  const isCurrentPage = (item: CommandItem) =>
    item.href !== '' && (currentPath === item.href || currentPath.startsWith(item.href + '/'))

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          onEscapeKeyDown={(e) => {
            // Esc no submenu volta para a raiz em vez de fechar a paleta.
            if (view !== 'root') {
              e.preventDefault()
              setSearch('')
              setView('root')
            }
          }}
          className="fixed left-1/2 top-[20vh] z-50 w-full max-w-lg -translate-x-1/2 rounded-lg border border-border bg-background shadow-lg duration-150 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-4"
        >
          <DialogPrimitive.Title className="sr-only">
            Navegação rápida
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Busque e navegue rapidamente
          </DialogPrimitive.Description>

          <Command label="Navegação rápida" className="flex flex-col" loop>
            <div className="flex items-center gap-2 border-b border-border px-3">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <Command.Input
                placeholder={view === 'agentes' ? 'Buscar agente...' : 'Buscar...'}
                className="flex h-11 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
                autoFocus
                value={search}
                onValueChange={setSearch}
                onKeyDown={handleInputKeyDown}
              />
              <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border border-border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground sm:inline-flex">
                ESC
              </kbd>
            </div>

            <Command.List className="max-h-[300px] overflow-y-auto p-1">
              <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
                Nada com esse termo. Tente outro?
              </Command.Empty>

              {view === 'agentes' ? (
                <Command.Group heading="Trocar de agente" className={groupHeadingClassName}>
                  {agentSwitchItems.map(item => (
                    <CommandPaletteItem
                      key={item.id}
                      item={item}
                      isCurrent={false}
                      onSelect={handleSelect}
                    />
                  ))}
                </Command.Group>
              ) : (
                groups.map(group => (
                  <Command.Group key={group.key} heading={group.heading} className={groupHeadingClassName}>
                    {group.items.map(item => (
                      <CommandPaletteItem
                        key={item.id}
                        item={item}
                        isCurrent={isCurrentPage(item)}
                        onSelect={handleSelect}
                      />
                    ))}
                  </Command.Group>
                ))
              )}
            </Command.List>

            <div className="flex items-center gap-4 border-t border-border px-3 py-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <kbd className="rounded border border-border bg-muted px-1 font-mono text-[10px]">↑</kbd>
                <kbd className="rounded border border-border bg-muted px-1 font-mono text-[10px]">↓</kbd>
                navegar
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded border border-border bg-muted px-1 font-mono text-[10px]">↵</kbd>
                abrir
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded border border-border bg-muted px-1 font-mono text-[10px]">esc</kbd>
                fechar
              </span>
            </div>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

function CommandPaletteItem({
  item,
  isCurrent,
  onSelect,
}: {
  item: CommandItem
  isCurrent: boolean
  onSelect: (value: string) => void
}) {
  const Icon = item.icon

  return (
    <Command.Item
      value={item.id}
      keywords={item.keywords}
      onSelect={onSelect}
      aria-current={isCurrent ? 'page' : undefined}
      className={cn(
        'group flex items-center gap-3 rounded-md px-2 py-2 text-sm cursor-pointer transition-colors',
        'text-foreground',
        'data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground',
      )}
    >
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
      <span className="flex-1 truncate">
        {item.label}
        {item.subtitle && (
          <span className="ml-2 text-xs text-muted-foreground">{item.subtitle}</span>
        )}
      </span>
      {isCurrent && (
        <span className="shrink-0 text-xs text-muted-foreground">Página atual</span>
      )}
      {item.shortcut ? (
        <kbd className="pointer-events-none hidden h-5 shrink-0 select-none items-center rounded border border-border bg-muted px-1 font-mono text-[10px] font-medium text-muted-foreground sm:inline-flex">
          {item.shortcut}
        </kbd>
      ) : (
        !isCurrent && (
          // Dica ⏎ só no item destacado (via data-selected do cmdk), padrão cmdk/shadcn.
          <kbd
            aria-hidden="true"
            className="pointer-events-none hidden h-5 shrink-0 select-none items-center rounded border border-border bg-muted px-1 font-mono text-[10px] font-medium text-muted-foreground sm:group-data-[selected=true]:inline-flex"
          >
            ⏎
          </kbd>
        )
      )}
    </Command.Item>
  )
}
